import {
    Preloader, Logger, Utils,
    FileUtils, NumberUtils,
    UriHelper, StringUtils,
    PreloaderInfo, ObjectUtils,
    StringsFactory, StringsFactoryInitializeOptions,
    PreloaderProviderBase, ArgValidator
} from 'neo-common';
import {GameDefinitionBase} from './GameDefinitionBase';
import {GameConfigurationBase} from './GameConfigurationBase';
import {AppContextBase, updateClientHost as updateClientHostForAppContext} from './AppContextBase';
import {AppBaseOptionsType, AppLoadResult, AppLoadStatus, IGameConfigurationProxy} from './Types';
import {ClientHostConsumerBase, updateClientHost} from '../ClientHost/ClientHostConsumerBase';
//import {ClientHostManager} from '../ClientHost/ClientHostManager';
import {IClientHost} from '../ClientHost/IClientHost';
import {IApp} from './IApp';
import {IClientHostConsumer} from '../ClientHost/IClientHostConsumer';
import {ClientHostFactory} from '../ClientHost/ClientHostFactory';

const DefaultAppBaseOptions: AppBaseOptionsType = {
    preloaderOptions: {
        file: 'files.xml',
        versionFile: 'version.txt',
        progressTextElementId: 'progressNumber',
        progressBarContainerElementId: 'progressBarCont',
        progressBarElementId: 'progressBar',
        preloaderProvider: null
    },
    gameDefinition: {
        file: 'definition.json'
    },
    gameConfiguration: {
        file: 'configuration.json'
    },
    clientHost: {
        defaultClientHostType: 'Default'
    }
};

/**
 * Base application construction
 */
export abstract class AppBase<TGameConfiguration extends GameConfigurationBase,
    TGameDefinition extends GameDefinitionBase,
    TClientHostConsumer extends ClientHostConsumerBase> implements IApp {

    protected _preloader: Preloader;
    protected _options: AppBaseOptionsType;
    protected _loadResult: AppLoadResult;
    protected _gameDefinition: TGameDefinition;
    protected _gameConfiguration: TGameConfiguration;
    protected _appContext: AppContextBase<TGameConfiguration, TGameDefinition>;
    protected _clientHostConsumer: TClientHostConsumer;
    protected _clientHost: IClientHost;

    constructor() {
        this._loadResult = {
            status: AppLoadStatus.Unknown,
            error: null
        };

        this.stringsLoadCompleted = this.stringsLoadCompleted.bind(this);

        this._options = ObjectUtils.clone(DefaultAppBaseOptions);
        this._options.preloaderOptions.preloaderProvider = this.createPreloaderProvider();
    }

    protected abstract get supportsRealityCheck(): boolean;

    /**
     * Loads application.
     */
    public load(): Promise<any> {
        this._loadResult.status = AppLoadStatus.Loading;
        this._loadResult.error = null;

        return this.prepareLoad()
            .then(canLoad => {
                if (!canLoad) {
                    return;
                }

                return this.loadVersion()
                    .then(() => this.loadStrings())
                    .then(() => this.loadGameDefinition())
                    // load of client host be be after 'load of game definition' (where is defined client host type to use)
                    // but before 'load game configuration' !
                    .then(() => this.loadClientHost())
                    .then(() => this.loadGameConfiguration())
                    .then(() => this.initAppContext())

                    .then(() => this.internalInitClientHost())
                    .then(() => this.updateGameConfiguration())
                    .then(() => this.loadResources())

                    /*.then(() => this.loadResources())  !! original !!
                    .then(() => this.internalInitClientHost())*/

                    .catch(err => this.handleLoadError(err))
                    .finally(() => this.loadCompleted());
            });
    }

    /**
     * Gets preloader version id
     * @returns {string} version id.
     */
    public get preloaderVersionId(): string {
        return this._preloader ? this._preloader.versionId : null;
    }

    /**
     * Determine whenever loading succeeded or not.
     * @returns {boolean} true when loading succeeded, false when loading does not finish to success.
     */
    public get loadingSucceed(): boolean {
        return this._loadResult.status === AppLoadStatus.Success;
    }

    /**
     * Determine whenever application has successfully loaded game configuration.
     * @returns {boolean} true if game configuration was successfully loaded or false on error.
     */
    protected get hasGameConfiguration(): boolean {
        return !Utils.isNullOrUndefined(this._gameConfiguration);
    }

    /**
     * Determine whenever application has successfully loaded game definition.
     * @returns {boolean} true if game definition was successfully loaded or false on error.
     */
    protected get hasGameDefinition(): boolean {
        return !Utils.isNullOrUndefined(this._gameDefinition);
    }

    /**
     * Gets game definition.
     * @returns {TGameDefinition}
     */
    public get gameDefinition(): TGameDefinition {
        return this._gameDefinition;
    }

    /**
     * Gets game configuration
     * @returns {TGameConfiguration}
     */
    public get gameConfiguration(): TGameConfiguration {
        return this._gameConfiguration;
    }

    /**
     * Gets client host.
     * @returns {IClientHost}
     */
    public get clientHost(): IClientHost {
        return this._clientHost;
    }

    /**
     * Method called within @load() before load promise actually begins.
     * @returns Promise<{boolean}> fulfilled with boolean true to continue load or false to stop load.
     */
    protected abstract prepareLoad(): Promise<boolean>

    /**
     * Gets application type name (e.g., 'Game' or 'Paytable')
     */
    protected abstract get appTypeName(): string;

    /**
     * creates preloader provider for application.
     * @returns created new instance which is descendant of @PreloaderProviderBase.
     */
    protected abstract createPreloaderProvider(): PreloaderProviderBase;

    /**
     * creates game definition instance.
     * @param definition - JSON containing game definition.
     * @returns created new instance of type TGameDefinition.
     */
    protected abstract createGameDefinition(definition: Object): TGameDefinition;

    /**
     * creates game configuration instance.
     * @param configuration - JSON containing game configuration.
     * @returns created new instance of type TGameConfiguration.
     */
    protected abstract createGameConfiguration(configuration: Object): TGameConfiguration;

    /**
     * creates app context instance.
     * @returns created new instance which is descendant of @AppContextBase<TGameConfiguration, TGameDefinition>.
     */
    protected abstract createAppContext(): AppContextBase<TGameConfiguration, TGameDefinition>;

    /**
     * creates client host consumer.
     * @returns created new instance of consumer for client host.
     */
    protected abstract createClientHostConsumer(): TClientHostConsumer;

    /**
     * Implementator must call 'require(<path to strings.ts>)' where <path to strings.ts> must be real path for 'Strings.ts' file!
     */
    protected abstract requireStrings(): void;

    /**
     * Called when load completed (even if there was loading error)
     */
    protected abstract finalizeLoad(): void;

    protected updateGameConfiguration(): Promise<any> {
        return Promise.resolve()
            .then(() => {
                if (!Utils.isNullOrUndefined(this._clientHost)) {
                    const cfg = this._gameConfiguration as any;
                    if (cfg) {
                        return this._clientHost.updateGameConfiguration(cfg as IGameConfigurationProxy);
                    }
                }
            });
    }

    protected internalInitClientHost(): Promise<any> {
        return Utils.isNullOrUndefined(this._clientHost)
            ? Promise.resolve()
            : this._clientHost.initialize(this._appContext)
                .then(() => this.initClientHost())
                .then(() => this.applyChangesFromClientHost());
    }

    private applyChangesFromClientHost(): Promise<any> {
        return Promise.resolve()
            .then(() => {
                if (!Utils.isNullOrUndefined(this._clientHost)) {
                    // apply changes (if there are any) from client, e.g. if current language of client host differs from current language of game
                    const clientHostLanguage = this._clientHost.getLanguage();
                    if (!Utils.isNullOrUndefined(clientHostLanguage) && StringsFactory.currentLanguage !== clientHostLanguage) {
                        if (DEBUG) {
                            let clientHostType = this._gameDefinition.ClientHostType;
                            Logger.debugGroup(`${this.loggerGroup}/applyChangesFromClientHost`,
                                `Client host '${clientHostType}' returns different lang '${clientHostLanguage}' than current '${StringsFactory.currentLanguage}', ` +
                                `changing to new lang '${clientHostLanguage}' now...`);
                        }

                        return StringsFactory.change(clientHostLanguage);
                    }
                }
            });
    }

    /**
     * Initialize previously loaded client host (if any)
     * @returns {Promise<any>} fulfilled promise after client host is initialized.
     */
    protected initClientHost(): Promise<any> {
        return Promise.resolve();
    }

    /**
     * Validate loaded client host
     */
    protected validateClientHost(): void {}

    /**
     * Gets logger group name
     * @returns {string}
     */
    protected get loggerGroup(): string {
        return `App-${this.appTypeName}`;
    }

    /**
     * Loads version from file defined in options via @PreloaderOptions.versionFile.
     * @returns {Promise<any>} fulfilled promise after version data is loaded.
     */
    protected loadVersion(): Promise<any> {
        if (!Utils.isNullOrUndefined(this._preloader)) {
            throw new Error(`${this.appTypeName} preloader is already created, could not be created more than once!`);
        }

        this._preloader = new Preloader(this._options.preloaderOptions);
        return this._preloader.loadVersion();
    }

    protected stringsLoadCompleted(lang: string): void {
        if (!Utils.isNullOrUndefined(this._appContext)) {
            if (DEBUG) {
                Logger.debugGroup(`${this.loggerGroup}/stringsLoadCompleted`, `Calling handleStringsLoadCompleted('${lang}')`);
            }

            this.handleStringsLoadCompleted(lang);
        } else {
            if (DEBUG) {
                Logger.debugGroup(`${this.loggerGroup}/stringsLoadCompleted`, `Skipping handleStringsLoadCompleted('${lang}') because AppContext is not created yet.`);
            }
        }
    }

    /**
     * Called when strings resources for specified lang was loaded.
     * @param {string} lang
     */
    protected abstract handleStringsLoadCompleted(lang: string): void;

    /**
     * Loads localized strings from defined path and file pattern 'Strings.[en/sk/...].json'
     * @return Promise<any> fulfilled promise after strings loaded.
     */
    protected loadStrings(): Promise<any> {
        this.requireStrings();
        const self = this;

        const stringsOptions: StringsFactoryInitializeOptions = {
            versionId: this.preloaderVersionId,
            initLoadCallback: () => {
                const query = Utils.getURIQuery();
                const lang = Utils.valueOrDefault(query['lang'], '');
                return {
                    initialLanguage: lang
                };
            },
            loadComplete: self.stringsLoadCompleted
        };

        return StringsFactory.initialize(stringsOptions)
            .then(lang => {
                if (DEBUG) {
                    Logger.debugGroup(`${this.loggerGroup}/loadStrings`,
                        'strings loaded for lang: ' + lang);
                }
                return lang;
            })
            .catch(err => {
                if (DEBUG) {
                    Logger.errorGroup(`${this.loggerGroup}/loadStrings`,
                        'Error loading localized strings, ' + Utils.getErrorMsg(err));
                }

                throw err;
            });
    }

    /**
     * Loads game definition from definition file
     * @returns {Promise<any>} fulfilled promise after game definition is loaded.
     */
    protected loadGameDefinition(): Promise<any> {
        let gameDefinitionFileName = this._options.gameDefinition.file;

        if (DEBUG) {
            let overwriteFile = window['DEFINITION_FILE'];
            if (!StringUtils.isNullOrEmpty(overwriteFile)) {
                gameDefinitionFileName = overwriteFile;
                Logger.debugGroup(`${this.loggerGroup}/loadGameDefinition`,
                    `Found overwrite setting 'DEFINITION_FILE' in window, using value '${overwriteFile}' as definition file!`);
            }
        }

        gameDefinitionFileName = UriHelper.setTimeStamp(gameDefinitionFileName, this.preloaderVersionId);

        if (DEBUG) {
            Logger.debugGroup(`${this.loggerGroup}/loadGameDefinition`,
                `Loading game definition from file '${gameDefinitionFileName}' ...`);
        }

        return FileUtils.readLocalJsonFile(gameDefinitionFileName)
            .then(json => {
                if (!ObjectUtils.isNullOrEmpty(json)) {
                    this._gameDefinition = this.createGameDefinition(json);
                    if (DEBUG) {
                        Logger.debugGroup(`${this.loggerGroup}/loadGameDefinition`,
                            `Loaded game definition from file '${gameDefinitionFileName}': `,
                            json);
                    }
                } else {
                    if (DEBUG) {
                        Logger.errorGroup(`${this.loggerGroup}/loadGameDefinition`,
                            `Loaded game definition from file '${gameDefinitionFileName}' has empty content!`);
                    }
                }
            })
            .catch(err => {
                if (DEBUG) {
                    Logger.errorGroup(`${this.loggerGroup}/loadGameDefinition`,
                        `Error loading game definition from file '${gameDefinitionFileName}'.`,
                        err);
                }

                throw err;
            });
    }

    /**
     * Loads game configuration from 'local' (not server) URL.
     * @returns {Promise<any>} fulfilled promise after game configuration is loaded.
     */
    protected loadGameConfiguration(): Promise<any> {
        let configFile: string = null;
        let timestamp: string = null; // null -> will use date ticks as timestamp

        if (DEBUG) {
            Logger.debugGroup(`${this.loggerGroup}/loadGameConfiguration`,
                `Resetting preloaderVersionId to null to load latest game configuration.json (date ticks will be used)`);
        }

        if (DEBUG) {
            let overwriteFile = window['CONFIGURATION_FILE'];
            if (!StringUtils.isNullOrEmpty(overwriteFile)) {
                configFile = UriHelper.setTimeStamp(overwriteFile, timestamp);
                Logger.debugGroup(`${this.loggerGroup}/loadGameConfiguration`,
                    `Found overwrite setting 'CONFIGURATION_FILE' in window, using value '${overwriteFile}' as definition file!`);
            }
        }

        if (this.hasLocalStorageConfiguration()) {
            return this.loadGameConfigurationPacketJSON();
        } else {
            return this.loadGameConfigurationLocalFile(configFile, timestamp);
        }
    }

    private hasLocalStorageConfiguration(): boolean {
        if(UriHelper.getPacketsMode()) {
            if (UriHelper.getPacketSetName(Utils.getURIQuery()) === 'jsonData') {
                return localStorage.getItem('jsonData') !== null && localStorage.getItem('jsonData') !== '';
            }
        }

        return false;
    }

    private loadGameConfigurationPacketJSON(): Promise<any> {
        let data = localStorage.getItem('jsonData');

        try {
            let jsonData = JSON.parse(data);
            if (jsonData['Configuration'] != null) {
                this._gameConfiguration = this.createGameConfiguration(jsonData['Configuration']);
                return Promise.resolve(this._gameConfiguration);
            }
        } catch (err) {
            if (DEBUG) {
                Logger.errorGroup(`${this.loggerGroup}/loadGameConfiguration`,
                    `Error loading game configuration from local storage.`,
                    err);
            }

            throw err;
        }
    }

    private loadGameConfigurationLocalFile(configFile: string, timestamp: string): Promise<any> {
        if (configFile === null) {
            configFile = UriHelper.setTimeStamp(this._options.gameConfiguration.file, timestamp);
        }

        return FileUtils.readLocalJsonFile(configFile)
            .then(json => {
                if (!ObjectUtils.isNullOrEmpty(json)) {
                    this._gameConfiguration = this.createGameConfiguration(json);
                    if (DEBUG) {
                        Logger.debugGroup(`${this.loggerGroup}/loadGameConfiguration`,
                            `Loaded game configuration from file '${configFile}': `,
                            json);
                    }
                }

                return this._gameConfiguration;
            })
            .catch(err => {
                if (DEBUG) {
                    Logger.errorGroup(`${this.loggerGroup}/loadGameConfiguration`,
                        `Error loading game configuration.`,
                        err);
                }

                throw err;
            });
    }

    /**
     * initialize application context
     * @returns {Promise<void>} fulfilled promise after application context was successfully initialized.
     */
    protected initAppContext(): Promise<any> {
        this._appContext = this.createAppContext();

        if (Utils.isNullOrUndefined(this._appContext)) {
            throw new Error(
                `${this.loggerGroup} - createAppContext must return non-null new instance of AppContext.`);
        }

        // update client host private field on app context
        updateClientHostForAppContext(this._appContext, this._clientHost, this._clientHostConsumer);

        return Promise.resolve();
    }

    /**
     * Loads file resources defined in 'files.xml' using preloader.
     * @returns {Promise<any>} fulfilled promise after resources are loaded.
     */
    protected loadResources(): Promise<any> {
        let loadingGroups: Array<string> = [];

        if(this._gameConfiguration != null) {
            if (this._gameConfiguration.SplashScreen) {
                loadingGroups.push('SPLASH');
            }

            this.prepareGameSkin();
            this.loadExtraContent();
        }

        return this._preloader.load(loadingGroups);
    }

    protected prepareGameSkin(): void {
        if (this._gameConfiguration.GameSkin === '') {
            return;
        }

        window['GAME_SKIN'] = this._gameConfiguration.GameSkin;
        document.body.classList.add(`gameSkin-${this._gameConfiguration.GameSkin}`);
    }

    protected loadExtraContent(): void {
        if (this._gameConfiguration != null) {
            let extraContent: Array<string> = [];

            if (this._gameConfiguration.MaxButton) {
                let path = `Css/Extra/${window['QUALITY']}/maxButton.css`;

                if (DEBUG) {
                    if (window['DEV']) {
                        path = 'Platform/Desktop/' + path;
                    }
                }

                extraContent.push(path);
            }

            if (this._gameConfiguration.FRBFreeSpins) { //ToDo add configuration
                let path = `Css/Extra/FRBFreeSpins.css`;

                if (DEBUG) {
                    if (window['DEV']) {
                        path = `Quality/${window['QUALITY']}/` + path;
                    }
                }

                extraContent.push(path);
            }

            for (let i = 0; i < extraContent.length; i++) {
                this.addExtraCssContent(extraContent[i]);
            }
        }
    }

    protected addExtraCssContent(filePath: string): void {
        const head = document.head;
        const link = document.createElement('link');
        const mainLink = head.getElementsByTagName('link')[0];
        let version = "";

        if (mainLink != null) {
            let pathVersion = mainLink.getAttribute('href').split('?')[1];
            if (pathVersion != null) {
                version = `?${pathVersion}`;
            }
        }

        link.type = 'text/css';
        link.rel = 'stylesheet';
        link.href = `${filePath}${version}`;

        head.appendChild(link);
    }

    /**
     * Handle loading error.
     * @param err - error in load process.
     */
    protected handleLoadError(err: Error): void {
        this._loadResult.error = err;
        this._loadResult.status = AppLoadStatus.Failure;

        if (DEBUG) {
            Logger.errorGroup(this.loggerGroup, err);
        }
        /*else {
         throw err;
         }*/
    }

    /**
     * Loads client host.
     * @returns {Promise<any>} fulfilled promise after client host is loaded.
     */
    protected loadClientHost(): Promise<IClientHost> {
        return new Promise<IClientHost>((resolve: any, reject: any) => {
            const defaultClientHostType = this._options.clientHost.defaultClientHostType;
            const clientHostType = this._gameDefinition.ClientHostType;

            this._clientHostConsumer = this.createClientHostConsumer();
            if (Utils.isNullOrUndefined(this._clientHostConsumer)) {
                if (DEBUG) {
                    Logger.errorGroup(`${this.loggerGroup}/loadClientHost`,
                        'CreateClientHostConsumer must return non-null new instance of descendant from ClientHostConsumerBase type.');
                }

                throw new Error('Error loading client host');
            }

            try {
                //this._clientHost = ClientHostManager.initialize(clientHostType, this._clientHostConsumer);
                this._clientHost = this.createClientHost(clientHostType, this._clientHostConsumer);
            } catch (err) {
                if (DEBUG) {
                    let fallbackClientHostType = window['CLIENTHOST_TYPE'];
                    if (!StringUtils.isNullOrEmpty(fallbackClientHostType)) {
                        Logger.errorGroup(`${this.loggerGroup}/loadClientHost`,
                            `Error loading client host '${clientHostType}' (defined by game definition)` +
                            `, will try to load fallback client host '${fallbackClientHostType}' defined in WINDOW['CLIENTHOST_TYPE'] ...`);

                        //this._clientHost = ClientHostManager.initialize(fallbackClientHostType, this._clientHostConsumer);
                        this._clientHost = this.createClientHost(fallbackClientHostType, this._clientHostConsumer);

                        if (Utils.isNullOrUndefined(this._clientHost)) {
                            throw err;
                        }
                    }
                } else {
                    throw err;
                }
            }

            if (Utils.isNullOrUndefined(this._clientHost)) {
                if (DEBUG) {
                    Logger.errorGroup(`${this.loggerGroup}/loadClientHost`,
                        `Could not load client host '${clientHostType}' (defined by game definition)` +
                        `, will try to load default client host '${defaultClientHostType}'...`);
                }

                //this._clientHost = ClientHostManager.initialize(defaultClientHostType, this._clientHostConsumer);
                this._clientHost = this.createClientHost(defaultClientHostType, this._clientHostConsumer);
            }

            if (Utils.isNullOrUndefined(this._clientHost)) {
                if (DEBUG) {
                    Logger.errorGroup(`${this.loggerGroup}/loadClientHost`,
                        `Could not load any valid client host ('${clientHostType}' or '${defaultClientHostType}')!`);
                }

                throw new Error('Error loading client host');
            }

            this.validateClientHost();

            if (DEBUG) {
                Logger.debugGroup(`${this.loggerGroup}/loadClientHost`,
                    `Successfully initialized client host '${this._clientHost.type}' version '${this._clientHost.version}'` +
                    `, supports : ` + JSON.stringify(this._clientHost.supports));
            }

            return resolve(this._clientHost);
        });
    }

    private createClientHost(clientHostType: string, clientHostConsumer: IClientHostConsumer): IClientHost {
        ArgValidator.ensureStringIsNotNullOrEmpty(clientHostType, 'clientHostType');
        ArgValidator.ensureIsNotNullOrUndefined(clientHostConsumer, 'clientHostConsumer');

        const clientHost = ClientHostFactory.create(clientHostType, clientHostConsumer) as IClientHost;

        if (!Utils.isNullOrUndefined(clientHost)) {
            updateClientHost(clientHostConsumer, clientHost);
        }

        return clientHost;
    }


    /**
     * Method called after @load() completed.
     */
    protected loadCompleted(): void {
        let configsLoaded = this.hasGameConfiguration && this.hasGameDefinition;
        let preloaderSucceed = PreloaderInfo.instance.loadSucceed;
        let appLoadSucceed = preloaderSucceed && configsLoaded;

        if (this._loadResult.status === AppLoadStatus.Loading && appLoadSucceed) {
            this._loadResult.status = AppLoadStatus.Success;
        }

        if (DEBUG) {
            Logger.debugGroup(`${this.loggerGroup}/loadCompleted`,
                `Loading result: ` + AppLoadStatus[this._loadResult.status]);

            if (this.hasGameDefinition) {
                Logger.debugGroup(`${this.loggerGroup}/loadCompleted`,
                    `Using game definition: `, this._gameDefinition.getRaw());
            }

            if (this.hasGameConfiguration) {
                let testValue = 123.456;
                let fmtRes = NumberUtils.formatNumber(testValue, this._gameConfiguration.NumberFormat);
                Logger.debugGroup('GameConfiguration',
                    `Test number formatting of value '${testValue}' (format ` +
                    `'${this._gameConfiguration.NumberFormat}' from config) => '${fmtRes}'`);
            }
        }

        try {
            this.finalizeLoad();

            // notify client host that game was loaded (even when failed)
            if (this._clientHost) {
                if (DEBUG) {
                    let clientHostType = this._gameDefinition.ClientHostType;
                    Logger.debugGroup(`${this.loggerGroup}/loadCompleted`,
                        `Calling 'gameLoaded(${this.loadingSucceed})' on client host '${clientHostType}'`);
                }

                this._clientHost.gameLoaded(this);
            }

            if (this.supportsRealityCheck) {
                this.startRealityCheck();
            }

            this._preloader = null;

            if (DEBUG) {
                Logger.debugGroup(`${this.loggerGroup}/loadCompleted`,
                    `Preloader resources was cleaned up.`);
            }
        } catch (err) {
            if (DEBUG) {
                Logger.errorGroup(`${this.loggerGroup}/loadCompleted`,
                    `Calling 'finalizeLoad' failed on error`,
                    err);
            }
        }
    }

    protected abstract startRealityCheck(): void;

    /**
     * Exist current game
     */
    public abstract exit(): Promise<any>;

    /**
     * Shows/redirects to history dialog/url.
     */
    public abstract history(): Promise<any>;

    /**
     * Continues execution of paused game (after some dialog, etc).
     */
    public abstract continue(): Promise<any>;

    /**
     * Close reality check
     */
    public abstract closeRealityCheck(): Promise<any>;
}


/** WEBPACK FOOTER **
 ** ./Engine/GameEngine/AppBase.ts
 **/